// Playbook Phase B.3 — FULL PLAY LIFECYCLE FOUNDATION. Pure, framework-
// free helpers that let the coach-facing editor present ONE continuous
// scrubber spanning:
//
//   FORMATION → PRE-SNAP → SNAP → POST-SNAP → END
//
// while every EXISTING piece of Post-Snap math (playbookTimeline.js's
// keyframe/actor_state/interpolation engine, entirely unchanged by this
// phase) keeps operating on its own familiar 0..1 Post-Snap-local
// domain. This file is the seam between the two: it defines what
// fraction of the single coach-facing scrubber belongs to Pre-Snap vs.
// Post-Snap, and converts back and forth between "one big scrub number"
// and "which phase + how far into it."
//
// ─────────────────────────────────────────────────────────────────────
// WHY A SEPARATE FILE (§63/§64 of the B.3 spec): playbookTimeline.js
// stays exactly what it already was — a single-phase keyframe engine
// with no knowledge that a Pre-Snap phase exists at all. This file
// COMPOSES it (via keyframesForPhase() below, which callers use to
// pre-filter `timeline.keyframes` before handing them to any
// playbookTimeline.js function) rather than replacing or duplicating
// any of its logic. TeamPlaybookPlayEditor.js is the only current
// caller; a future read-only Athlete Study surface can reuse every
// export here exactly as-is, same as it will reuse usePlaybackAnimation.js
// and playbookTimeline.js — nothing here is wired to any mutation
// handler.
//
// ─────────────────────────────────────────────────────────────────────
// THE FULL-SCRUB ↔ PHASE MAPPING IS PRESENTATION-ONLY (§20/§21/§55):
// PRE_SNAP_END_FRACTION is a UI allocation of scrubber real estate, NOT
// football timing. It is never persisted anywhere in a Play document —
// every stored position (a Pre-Snap or Post-Snap Moment's `position`,
// or a Post-Snap actor_state `progress`) remains phase-LOCAL 0..1,
// completely independent of whatever fraction of the on-screen slider
// that phase happens to occupy today. A future pass introducing real
// per-Play timing/duration can replace this fixed-fraction mapping
// entirely without rewriting a single stored Play — this file is
// exactly the one seam that would need to change, nothing downstream of
// it.
// ─────────────────────────────────────────────────────────────────────

import { POSITION_MATCH_TOLERANCE } from './playbookTimeline';

// §22 of the B.3 spec's own recommended v1 weighting: Pre-Snap gets a
// visibly SMALLER share of the scrubber than Post-Snap (a shift/motion
// is a brief prelude, not half the play). 0.25 chosen as a clean, easy-
// to-eyeball quarter-mark — not derived from any real timing data.
export const PRE_SNAP_END_FRACTION = 0.25;

// The three fixed anchor positions on the FULL 0..1 lifecycle scrubber.
// FORMATION_FULL_SCRUB is always 0 and END_FULL_SCRUB is always 1 by
// construction (the scrubber's own domain); SNAP_FULL_SCRUB is wherever
// PRE_SNAP_END_FRACTION currently puts it — kept as its own named
// export so callers never hardcode the fraction twice.
export const FORMATION_FULL_SCRUB = 0;
export const SNAP_FULL_SCRUB = PRE_SNAP_END_FRACTION;
export const END_FULL_SCRUB = 1;

/** Full-lifecycle scrub position (0=Formation..1=End) → which phase the
 * coach is currently looking at, plus how far into THAT phase's own
 * 0..1 domain they are. At the exact Snap boundary this resolves to
 * `{ phase: 'post_snap', progress: 0 }` — Snap is defined as the FIRST
 * instant of Post-Snap, matching playbookTimeline.js's own existing
 * `kind: 'snap'` keyframe fixed at post_snap position 0 (see §17 of the
 * spec: "at Snap, Post-Snap progress is 0"). This is what makes the
 * dedicated Snap button ( setFullScrub(SNAP_FULL_SCRUB) ) land on
 * EXACTLY the same resolved state as the pre-existing Snap keyframe
 * always has, with zero extra bookkeeping. */
export function fullScrubToLifecycle(fullScrub) {
  const clamped = Math.max(0, Math.min(1, fullScrub));
  // Phase B.3a bugfix: was `clamped <= PRE_SNAP_END_FRACTION`, which put
  // the EXACT Snap boundary itself in the pre_snap branch (progress: 1)
  // — contradicting this very function's own header comment above
  // ("at the exact Snap boundary this resolves to
  // `{phase:'post_snap', progress:0}`"). Every numeric fullScrub this
  // function has ever produced/consumed downstream was unaffected by
  // the off-by-one (postSnapProgress/preSnapProgress in
  // TeamPlaybookPlayEditor.js both special-case the non-active phase to
  // a fixed 0/1 already, so the two branches happened to agree
  // numerically) — but `phase` itself being wrong at exactly Snap meant
  // `selectedKeyframe` incorrectly missed the real reserved Snap
  // keyframe there, and — the reason this needed fixing THIS pass —
  // Phase B.3a's new art-visibility gate reads `phase` directly, so
  // Snap must unambiguously classify as `post_snap` for "at Snap, show
  // Post-Snap art" (§13/§41) to be correct. `<` (strict) now matches
  // the documented contract exactly.
  if (clamped < PRE_SNAP_END_FRACTION) {
    const progress = PRE_SNAP_END_FRACTION > 0 ? clamped / PRE_SNAP_END_FRACTION : 1;
    return { phase: 'pre_snap', progress: Math.max(0, Math.min(1, progress)) };
  }
  const span = 1 - PRE_SNAP_END_FRACTION;
  const progress = span > 0 ? (clamped - PRE_SNAP_END_FRACTION) / span : 1;
  return { phase: 'post_snap', progress: Math.max(0, Math.min(1, progress)) };
}

/** Inverse of fullScrubToLifecycle() — a phase + phase-local progress
 * back to one full-lifecycle scrub number. */
export function lifecycleToFullScrub(phase, progress) {
  const p = Math.max(0, Math.min(1, progress));
  if (phase === 'pre_snap') return p * PRE_SNAP_END_FRACTION;
  return PRE_SNAP_END_FRACTION + p * (1 - PRE_SNAP_END_FRACTION);
}

export function isAtFormation(fullScrub, tolerance = POSITION_MATCH_TOLERANCE) {
  return Math.abs(fullScrub - FORMATION_FULL_SCRUB) <= tolerance;
}
export function isAtSnap(fullScrub, tolerance = POSITION_MATCH_TOLERANCE) {
  return Math.abs(fullScrub - SNAP_FULL_SCRUB) <= tolerance;
}
export function isAtEnd(fullScrub, tolerance = POSITION_MATCH_TOLERANCE) {
  return Math.abs(fullScrub - END_FULL_SCRUB) <= tolerance;
}

/** A stored keyframe's phase — coach-created Moments saved before B.3
 * existed never had a `phase` field at all; §42 of the spec requires
 * interpreting every one of those as `'post_snap'` (they were always
 * Post-Snap Moments, back when Post-Snap was the only phase that
 * existed), so absence defaults there rather than to `'pre_snap'`. Only
 * a genuine Pre-Snap Moment (created by this phase's own new UI) ever
 * carries `phase: 'pre_snap'` explicitly. Snap/End's own reserved
 * keyframes are always implicitly `'post_snap'` too (never tagged, same
 * default) — they represent the post_snap-local 0/1 boundary exactly as
 * before. */
export function keyframePhase(keyframe) {
  return keyframe?.phase === 'pre_snap' ? 'pre_snap' : 'post_snap';
}

/** Every stored keyframe belonging to one phase — the ONE place a
 * caller should filter `timeline.keyframes` before handing them to any
 * playbookTimeline.js function (sortedKeyframes/findKeyframeAtPosition/
 * bracketKeyframesForPosition), since those all stay deliberately
 * phase-oblivious pure array ops (see this file's own header note). */
export function keyframesForPhase(timeline, phase) {
  return (timeline?.keyframes || []).filter(k => keyframePhase(k) === phase);
}

/** Nudge-earlier/nudge-later (§ of the B.2.1 UX pass, extended for B.3):
 * moves by `deltaProgress` (positive = later, negative = earlier) within
 * the CURRENT phase's own progress domain — not by a fixed fraction of
 * the full scrubber — so nudge precision stays identical to what it
 * always was for Post-Snap (a step is always "1% of THIS phase," never
 * "1% of the whole lifecycle," which would make Pre-Snap nudges four
 * times coarser than Post-Snap ones purely because Pre-Snap occupies a
 * smaller slice of the scrubber). Crossing a phase boundary carries the
 * overflow through rather than clamping dead at the edge — nudging
 * later from deep in Pre-Snap eventually crosses into Post-Snap
 * starting from its own 0, and nudging earlier from early Post-Snap
 * crosses back into late Pre-Snap — so the nudge buttons feel like one
 * continuous control across Snap, matching the "no mode switching"
 * acceptance test. Formation/End themselves still clamp (there is
 * nothing before Formation or after End). */
export function nudgeFullScrub(fullScrub, deltaProgress) {
  const { phase, progress } = fullScrubToLifecycle(fullScrub);
  let nextPhase = phase;
  let nextProgress = progress + deltaProgress;
  if (nextProgress > 1) {
    if (phase === 'pre_snap') { nextPhase = 'post_snap'; nextProgress -= 1; }
    else nextProgress = 1;
  } else if (nextProgress < 0) {
    if (phase === 'post_snap') { nextPhase = 'pre_snap'; nextProgress += 1; }
    else nextProgress = 0;
  }
  nextProgress = Math.max(0, Math.min(1, nextProgress));
  return lifecycleToFullScrub(nextPhase, nextProgress);
}

// ─────────────────────────────────────────────────────────────────────
// Phase B.3a — PLAYBACK PACING. §3 of the B.3a spec: automated Play/
// Pause previously advanced `fullScrub` at one constant rate, so
// Pre-Snap (only 25% of the fullScrub 0..1 span, per
// PRE_SNAP_END_FRACTION) visibly raced through in a quarter of the time
// Post-Snap got — a presentation bug, not a geography one. The fix
// keeps the SCRUBBER GEOGRAPHY exactly as-is (Snap still visually sits
// at PRE_SNAP_END_FRACTION — §4: "do not solve this by moving Snap to
// 0.5") and instead decouples PLAYBACK TIME from scrubber distance:
// each phase gets its own fixed presentation duration for traversing
// its own FULL 0..1 phase-local span, independent of how much of the
// visual scrubber that phase occupies.
//
// Presentation-only UI pacing — same contract as PTP_PREVIEW_DURATION_MS
// (PlaybookTimelineControls.js) and usePlaybackAnimation.js's own
// `durationMs`: NOT football timing, NEVER persisted, not read anywhere
// except by advanceFullScrub() below. Pre-Snap deliberately gets a
// SHORTER preview duration than Post-Snap (a shift/motion is naturally
// a brief prelude — same "smaller share" judgment call §22 of B.3 made
// for scrubber geography, now made independently for pacing).
export const PRE_SNAP_PREVIEW_DURATION_MS = 2800;
export const POST_SNAP_PREVIEW_DURATION_MS = 4500;

/** Phase-aware playback position mapping — the `mapProgress` callback
 * usePlaybackAnimation.js's generalized hook accepts (see that file's
 * own updated header note). Pure function of `from` (the fullScrub
 * position playback started/resumed at) and `elapsedMs` (time since
 * that start) — returns the new fullScrub position plus whether
 * playback has reached End.
 *
 * Mirrors the ORIGINAL single-phase hook's own "remaining distance
 * scales the remaining time" pattern (see usePlaybackAnimation.js's
 * header note on resuming mid-play), just applied per-phase with a
 * carry-over across the Snap boundary instead of one linear pass over
 * the whole 0..1 range:
 *   - Starting in Pre-Snap: the REMAINING Pre-Snap fraction gets a
 *     proportional slice of `preSnapDurationMs` (starting at 50%
 *     through Pre-Snap uses roughly half that duration) — once that
 *     elapses, playback falls through into Post-Snap starting fresh at
 *     its own progress 0, getting the FULL `postSnapDurationMs` (§6:
 *     "then full Post-Snap duration").
 *   - Starting in Post-Snap: only the REMAINING Post-Snap fraction gets
 *     a proportional slice of `postSnapDurationMs` — no Pre-Snap replay.
 * `lifecycleToFullScrub()` converts each phase-local result back onto
 * the SAME fixed scrubber geography this file already defines — pacing
 * changes never move Snap's visual position. */
export function advanceFullScrub({ from, elapsedMs, preSnapDurationMs, postSnapDurationMs }) {
  const { phase, progress } = fullScrubToLifecycle(from);

  if (phase === 'pre_snap') {
    const remainingPreSnapSpan = 1 - progress;
    const remainingPreSnapMs = preSnapDurationMs * remainingPreSnapSpan;
    if (elapsedMs < remainingPreSnapMs) {
      const nextProgress = preSnapDurationMs > 0 ? progress + elapsedMs / preSnapDurationMs : 1;
      return { position: lifecycleToFullScrub('pre_snap', nextProgress), done: false };
    }
    // Crossed Snap mid-tick — the leftover time (past the remaining
    // Pre-Snap slice) flows straight into a FRESH Post-Snap run, exactly
    // as if Play had been pressed again right at Snap.
    const postElapsedMs = elapsedMs - remainingPreSnapMs;
    const postProgress = postSnapDurationMs > 0 ? postElapsedMs / postSnapDurationMs : 1;
    if (postProgress >= 1) return { position: END_FULL_SCRUB, done: true };
    return { position: lifecycleToFullScrub('post_snap', postProgress), done: false };
  }

  // phase === 'post_snap'
  const remainingPostSnapSpan = 1 - progress;
  const remainingPostSnapMs = postSnapDurationMs * remainingPostSnapSpan;
  if (elapsedMs >= remainingPostSnapMs) return { position: END_FULL_SCRUB, done: true };
  const nextProgress = postSnapDurationMs > 0 ? progress + elapsedMs / postSnapDurationMs : 1;
  return { position: lifecycleToFullScrub('post_snap', nextProgress), done: false };
}

// Phase B.3e briefly added a fixed real-time "Moment decision cue
// visibility window" here (CUE_VISIBILITY_WINDOW_MS/
// CUE_VISIBILITY_HALF_WINDOW/isMomentCueVisible()) so a Passing Read cue
// would flash on/off around the Moment it was attached to. Phase B.3f
// REMOVED this entirely and re-derived visibility from the target
// receiver's own resolved ROUTE PROGRESS against a stored start/end
// window instead — browser testing then showed that two-tap route-
// window authoring was too many interactions for the tablet-first
// coaching workflow (§1 of the B.3g spec). Phase B.3g REINSTATES
// Moment-based timing (this time for real, not "fake" — the whole
// point of B.3g's product direction is that a Passing Read genuinely
// IS an action owned by a Moment: "WHEN does the QB evaluate this
// read?"), while keeping B.3f's one good idea — the dashed cue line
// points at a route-attached LANDMARK, not the receiver marker — by
// storing a single captured `route_progress` on the cue instead of a
// two-point window (see TeamPlaybookPlayEditor.js's own
// resolveActorRouteProgressAtFullScrub(), commitPassingRead(), and
// visiblePassingReadCue computation).
//
// READ_CUE_HALF_WINDOW_POST_SNAP is a presentation-only real-time flash
// window around a Passing Read's owning Moment — NOT football timing,
// never persisted anywhere, purely how long the teaching cue stays
// visible during Play. The B.3g spec's own suggested starting point
// (±2%, i.e. 0.02) works out to roughly 0.04 * POST_SNAP_PREVIEW_DURATION_MS
// (~180ms) of real playback time at the current post-snap pacing — too
// brief to reliably perceive over a rAF-driven animation. 0.05 was
// chosen instead: a ~450ms flash (0.10 * 4500ms), long enough to read
// as "the cue briefly appeared" without lingering long enough to feel
// like a persistent HUD element. At the EXACT Moment position (paused,
// or landed on via the Moment dot / "Jump to Moment"), distance is 0,
// which is trivially within any positive half-window — so "stays
// visible indefinitely while paused exactly there" falls out of this
// same check for free, no special-case branch needed.
export const READ_CUE_HALF_WINDOW_POST_SNAP = 0.05;

/** Is a Passing Read cue owned by a Moment at `momentPostSnapPosition`
 * currently within its brief presentation window, given the coach's
 * CURRENT post-snap-local progress? Both arguments are already
 * phase-local (post_snap) 0..1 values — never raw `fullScrub` — exactly
 * like every other post-snap-local comparison in this file. */
export function isMomentCueVisible(momentPostSnapPosition, currentPostSnapProgress) {
  return Math.abs(momentPostSnapPosition - currentPostSnapProgress) <= READ_CUE_HALF_WINDOW_POST_SNAP;
}
